import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  Table,
  Select,
  Badge,
  Tooltip,
} from "antd"
import { CheckCircleFilled, CommentOutlined } from "@ant-design/icons"
import * as API from "common/api"
import * as COMMONS from "common/common"
import { ChatModalComponent } from "components"
import moment from "moment"
import "moment/locale/ja"
import React, { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useQuery, useQueryClient} from "react-query"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import useResizeObserver from "@react-hook/resize-observer"

moment.updateLocale("ja")

const CustomTable = styled(Table)`
  .ant-table-ping-right,
  .ant-table-ping-left tr th.ant-table-cell {
    cursor: grab;
    cursor: -webkit-grab;
  }

  tr.active th.ant-table-cell {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }
`

const useSize = (target) => {
  const [size, setSize] = React.useState()

  React.useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry.contentRect))
  return size
}

const { Option } = Select

const Customers = (props) => {
  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const [customersFilterForm] = Form.useForm()
  const [chatForm] = Form.useForm()

  const tableRef = useRef(null)
  const size = useSize(tableRef)
  const queryClient = useQueryClient()
  const [chats, setChats] = useState([])
  const [customers, setCustomers] = useState([])
  const [currentCustomer, setCurrentCustomer] = useState([])
  
  const [isScrollableHintVisible, setIsScrollableHintVisible] = useState(false)
  const [isChatModalVisible, setIsChatModalVisible] = useState(false)

  const [paginationPerPage, setPaginationPerPage] = useState(20)
  const [paginationPage, setPaginationPage] = useState(1)
  const [paginationTotal, setPaginationTotal] = useState(0)
  const [paginationSort, setPaginationSort] = useState("desc")
  const [paginationSortKey, setPaginationSortKey] = useState("userId")

  const [filterUserCode, setFilterUserCode] = useState(undefined)
  const [filterName, setFilterName] = useState(undefined)
  const [filterEmail, setFilterEmail] = useState(undefined)
  const [filterTelephone, setFilterTelephone] = useState(undefined)
  const [filterAddress, setFilterAddress] = useState(undefined)
  const [filterLineSynced, setFilterLineSynced] = useState(undefined)
  const [filterIsFriend, setFilterIsFriend] = useState(undefined)
  const [filterNotificationPref, setFilterNotificationPref] =
    useState(undefined)

  const showChatModal = (customer) => {
    setCurrentCustomer(customer)
    setIsChatModalVisible(true)
    

  }

  const hideChatModal = () => {
    chatForm.resetFields()
    setChats([])
    setCurrentCustomer(undefined)
    setIsChatModalVisible(false)
    queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_CUSTOMERS);
  }
  useQuery(
    [
      API.QUERY_KEY_ADMIN_CUSTOMERS,
      {
        paginationPerPage: paginationPerPage,
        paginationPage: paginationPage,
        paginationSort: paginationSort,
        paginationSortKey: paginationSortKey,
        filterUserCode: filterUserCode,
        filterName: filterName,
        filterEmail: filterEmail,
        filterTelephone: filterTelephone,
        filterAddress: filterAddress,
        filterLineSynced: filterLineSynced,
        filterIsFriend: filterIsFriend,
        filterNotificationPref: filterNotificationPref,
      },
    ],
    () =>
      API.ADMIN_GET_CUSTOMERS(
        paginationPerPage,
        paginationPage,
        paginationSort,
        paginationSortKey,
        filterUserCode,
        filterName,
        filterEmail,
        filterTelephone,
        filterAddress,
        filterLineSynced,
        filterIsFriend,
        filterNotificationPref
      ),
    {
      keepPreviousData: true,
      onSuccess: (response) => {
        if (isMountedRef.current) {
          if (response?.data?.rows && response?.data?.rows?.length > 0) {
            setCustomers(response.data.rows)
          } else {
            setCustomers([])
          }

          setPaginationPerPage(response.data.pp || 20)
          setPaginationPage(response.data.p || 1)
          setPaginationTotal(response?.data?.count || 0)
          setPaginationSort(response?.data?.sort || "desc")
          setPaginationSortKey(response?.data?.sortKey || "userId")
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  useEffect(() => {
    const slider = document.querySelector("thead.ant-table-thead tr")
    const body = document.querySelector(".ant-table-container .ant-table-body")
    let isDown = false
    let startX
    let scrollLeft

    slider.addEventListener("mousedown", (e) => {
      isDown = true
      slider.classList.add("active")
      startX = e.pageX - body.offsetLeft
      scrollLeft = body.scrollLeft
    })

    slider.addEventListener("mouseleave", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mouseup", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) {
        return
      }

      e.preventDefault()
      const x = e.pageX - body.offsetLeft
      const walk = (x - startX) * 3 //scroll-fast
      body.scrollLeft = scrollLeft - walk
    })
  }, [])

  useEffect(() => {
    if (
      tableRef?.current?.querySelector(".ant-table-ping-right") ||
      tableRef?.current?.querySelector(".ant-table-ping-left")
    ) {
      setIsScrollableHintVisible(true)
    } else {
      setIsScrollableHintVisible(false)
    }
  }, [size])


  const customerColumns = [
    {
      key: "会員コード",
      title: <p className="whitespace-pre-wrap">{`会員\nコード`}</p>,
      dataIndex: "userCode",
      align: "center",
      width: 70,
    },
    {
      key: "氏名",
      title: "氏名",
      dataIndex: "customer",
      align: "center",
      width: 120,
      render: (customer) => (
        <>
          <p>{`${customer?.lastName || "ー"} ${
            customer?.firstName || "ー"
          }`}</p>
          <p className="text-xs text-gray-400">{`${
            customer?.lastNameKana || "ー"
          } ${customer?.firstNameKana || "ー"}`}</p>
        </>
      ),
    },
    {
      key: "メールアドレス",
      title: <p className="whitespace-pre-wrap">{`メール\nアドレス`}</p>,
      dataIndex: "email",
      align: "center",
      width: 100,
    },
    {
      key: "電話番号",
      title: "電話番号",
      dataIndex: "telephone",
      align: "center",
      width: 110,
    },
    {
      key: "住所",
      title: "住所",
      dataIndex: "customer",
      align: "center",
      width: 100,
      render: (customer) =>
        customer?.zipPostal || customer?.address
          ? `〒${
              customer?.zipPostal
                ? COMMONS.INSERT_CHARACTER(customer.zipPostal, 3, "-")
                : ""
            } ${customer?.address || ""}`
          : "ー",
    },
    {
      key: "LINE連携",
      title: <p className="whitespace-pre-wrap">{`LINE\n連携`}</p>,
      dataIndex: "displayName",
      align: "center",
      width: 100,
      render: (displayName) =>
        displayName ? (
          <div className="p-1 rounded bg-custom-green">
            <p className="text-white font-bold">
              <CheckCircleFilled className="mr-1" />
              連携済み
            </p>
          </div>
        ) : (
          "ー"
        ),
    },
    {
      key: "LINE友だち",
      title: <p className="whitespace-pre-wrap">{`LINE\n友だち`}</p>,
      dataIndex: "isFriend",
      align: "center",
      width: 60,
      render: (isFriend) =>
        isFriend ? (
          <CheckCircleFilled className="text-custom-green text-2xl" />
        ) : (
          "ー"
        ),
    },
    {
      key: "通知受信",
      title: <p className="whitespace-pre-wrap">{`通知\n受信`}</p>,
      dataIndex: "notificationPref",
      align: "center",
      width: 60,
      render: (notificationPref) =>
        COMMONS.NOTIFICATION_TYPES.find(
          (type) => type.value === notificationPref
        )?.text || "ー",
    },
    {
      key: "入会日",
      title: "入会日",
      dataIndex: "entryDate",
      align: "center",
      width: 130,
      render: (entryDate) =>
        entryDate ? moment(entryDate).format("YYYY年M月D日") : "ー",
    },
    {
      key: "action",
      title: "",
      dataIndex: "action",
      align: "center",
      width: 130,
      render: (customer) =>
        customer?.isFriend ? (
          <Badge count={customer?.unreadCount || 0}>
            <Tooltip title="LINEチャット" placement="top">
              <Button
                size="large"
                className="m-1"
                icon={<CommentOutlined rev={undefined} />}
                onClick={() => showChatModal(customer)}
              />
            </Tooltip>
          </Badge>
        ) : (
          ""
        ),
    },
  ]

  const handleTableChange = (paginationData, filters, sorter) => {
    if (paginationData.pageSize !== paginationPerPage) {
      setPaginationPerPage(paginationData.pageSize)
    }

    if (paginationData.current !== paginationPage) {
      setPaginationPage(paginationData.current)
    }

    if (sorter && sorter.order) {
      if (sorter.order === "ascend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("asc")
        }
      } else if (sorter.order === "descend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("desc")
        }
      }
    }

    if (sorter && sorter.field) {
      if (sorter.field !== paginationSortKey) {
        setPaginationSortKey(sorter.field)
      }
    }
  }

  const handleFilter = (data) => {
    if (data.filterUserCode !== filterUserCode) {
      setFilterUserCode(data.filterUserCode)
    }
    if (data.filterName !== filterName) {
      setFilterName(data.filterName)
    }
    if (data.filterEmail !== filterEmail) {
      setFilterEmail(data.filterEmail)
    }
    if (data.filterTelephone !== filterTelephone) {
      setFilterTelephone(data.filterTelephone)
    }
    if (data.filterAddress !== filterAddress) {
      setFilterAddress(data.filterAddress)
    }
    if (data.filterLineSynced !== filterLineSynced) {
      setFilterLineSynced(data.filterLineSynced)
    }
    if (data.filterIsFriend !== filterIsFriend) {
      setFilterIsFriend(data.filterIsFriend)
    }
    if (data.filterNotificationPref !== filterNotificationPref) {
      setFilterNotificationPref(data.filterNotificationPref)
    }
  }

  return (
    <>
      <Helmet>
        <title>会員一覧 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="会員一覧"
        className="animate__animated animate__fadeIn"
        bordered={false}
      >
        <Row>
          <Col xs={24}>
            <Form
              form={customersFilterForm}
              onFinish={handleFilter}
              size="large"
              initialValues={{
                filterUserCode: undefined,
                filterName: undefined,
                filterEmail: undefined,
                filterTelephone: undefined,
                filterAddress: undefined,
                filterLineSynced: undefined,
                filterIsFriend: undefined,
                filterNotificationPref: undefined,
              }}
            >
              <Row gutter={[16, 16]} justify="start">
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="会員コード"
                    name="filterUserCode"
                    className="mb-4"
                  >
                    <COMMONS.NUMERIC_INPUT
                      placeholder="00000000000000000028"
                      allowClear
                      pattern="[0-9]*"
                      inputMode="numeric"
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="氏名・氏名（フリガナ）"
                    name="filterName"
                    className="mb-4"
                  >
                    <Input placeholder="例：山田" allowClear />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="メールアドレス"
                    name="filterEmail"
                    className="mb-4"
                  >
                    <Input placeholder="例：yamada@gmail.com" allowClear />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="電話番号"
                    name="filterTelephone"
                    className="mb-4"
                  >
                    <Input placeholder="例：08036412545" allowClear />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="郵便番号 (ハイフン無し)・都道府県・住所"
                    name="filterAddress"
                    className="mb-4"
                  >
                    <Input placeholder="例：愛知県名古屋○○" allowClear />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="LINE連携"
                    name="filterLineSynced"
                    className="mb-4"
                  >
                    <Select
                      placeholder="LINE連携状態を選択してください"
                      allowClear
                    >
                      <Option value={1}>LINE連携済み</Option>
                      <Option value={0}>LINE連携してない</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="LINE友だち"
                    name="filterIsFriend"
                    className="mb-4"
                  >
                    <Select
                      placeholder="LINE友だち状態を選択してください"
                      allowClear
                    >
                      <Option value={1}>LINE友だちしている</Option>
                      <Option value={0}>LINE友だちしてない</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="通知受信"
                    name="filterNotificationPref"
                    className="mb-4"
                  >
                    <Select
                      placeholder="通知受信状態を選択してください"
                      allowClear
                    >
                      {COMMONS.NOTIFICATION_TYPES.map((nt) => (
                        <Option key={nt.value} value={nt.value}>
                          {nt.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 8]} justify="center" className="mt-8">
                <Col>
                  <Button type="primary" htmlType="submit" className="px-12">
                    検索
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Divider />
        <Tooltip
          visible={isScrollableHintVisible}
          placement="topRight"
          title="⇦スクロールできます⇨"
          overlayStyle={{
            zIndex: 999,
          }}
        >
          <CustomTable
            ref={tableRef}
            bordered
            size="small"
            columns={customerColumns}
            scroll={{
              x: 640,
              y: 720,
            }}
            locale={{ emptyText: "会員がありません" }}
            onChange={handleTableChange}
            pagination={{
              responsive: true,
              showSizeChanger: true,
              onShowSizeChange: (current, pageSize) =>
                setPaginationPerPage(pageSize),
              current: paginationPage,
              pageSize: paginationPerPage,
              total: paginationTotal,
              showTotal: (total, range) =>
                `全${total}件中${range[0]}～${range[1]}件目`,
              defaultCurrent: 1,
              defaultPageSize: 20,
              position: ["bottomRight"],
            }}
            dataSource={
              customers
                ? customers.map((customer) => {
                    return {
                      key: customer.userId,
                      userCode: customer.userCode || "ー",
                      customer: customer,
                      email: customer.email || "ー",
                      telephone: customer.telephone || "ー",
                      displayName: customer.displayName,
                      notificationPref: customer.notificationPref,
                      isFriend: customer.isFriend,
                      entryDate: customer.entryDate,
                      action: customer,
                    }
                  })
                : []
            }
          />
        </Tooltip>
      </Card>
      <ChatModalComponent
        {...props}
        isChatModalVisible={isChatModalVisible}
        hideChatModal={hideChatModal}
        chats={chats}
        setChats={setChats}
        chatForm={chatForm}
        currentMember={currentCustomer}
      />
    </>
  )
}

export default Customers
